import { addDays, startOfWeek } from "date-fns";

/**
 * Densidade vertical fixa do grid (1.6 px/min).
 * Apolonia_v2 usa 24px/15min = 1.6 px/min. Mantemos o mesmo rate independente
 * do slotDuration configurado — slots maiores ficam visualmente maiores mas
 * appointments têm altura consistente baseada em duração real.
 */
export const PIXELS_PER_MINUTE = 1.6;

/** Converte "HH:MM" pra minutos desde 00:00. */
export function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

/** Converte minutos desde 00:00 pra "HH:MM" (sem segundos). */
export function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Gera lista de labels de slots do grid, respeitando slotDuration. */
export function buildSlots(
  startHour: string,
  endHour: string,
  slotDurationMinutes: number,
): string[] {
  const startMin = timeToMinutes(startHour);
  const endMin = timeToMinutes(endHour);
  const slots: string[] = [];
  for (let m = startMin; m < endMin; m += slotDurationMinutes) {
    slots.push(minutesToTime(m));
  }
  return slots;
}

/** Altura total (px) do grid pra um range. */
export function totalGridHeight(startHour: string, endHour: string): number {
  return (timeToMinutes(endHour) - timeToMinutes(startHour)) * PIXELS_PER_MINUTE;
}

/** Posição Y (px) no grid pra um horário "HH:MM". */
export function positionYFor(hhmm: string, startHour: string): number {
  return (timeToMinutes(hhmm) - timeToMinutes(startHour)) * PIXELS_PER_MINUTE;
}

/** Altura (px) pra uma duração em minutos. */
export function heightForDuration(durationMinutes: number): number {
  return durationMinutes * PIXELS_PER_MINUTE;
}

/**
 * Retorna a posição Y do horário atual (dentro do range do grid),
 * ou null se o horário atual está fora do range visível.
 */
export function nowPositionY(startHour: string, endHour: string, now: Date): number | null {
  const minsNow = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  const startMin = timeToMinutes(startHour);
  const endMin = timeToMinutes(endHour);
  if (minsNow < startMin || minsNow > endMin) return null;
  return (minsNow - startMin) * PIXELS_PER_MINUTE;
}

/** Array de 7 datas da semana que contém `anchor`, começando em segunda. */
export function getWeekDays(anchor: Date): Date[] {
  const start = startOfWeek(anchor, { weekStartsOn: 0 });
  return Array.from({ length: 7 }).map((_, i) => addDays(start, i));
}

/** Retorna se duas datas são o mesmo dia (ignora hora). */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** A grade de horários com os extras que precisam aparecer (o horário atual da consulta, o de
 *  fechamento), sem repetir e em ordem. */
export function comHorarios(grade: string[], ...extras: (string | null | undefined)[]): string[] {
  const validos = extras.filter((h): h is string => !!h && /^\d{2}:\d{2}$/.test(h));
  return [...new Set([...grade, ...validos])].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));
}
